import React, { lazy, useEffect, useState, Fragment, Suspense } from 'react';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { connect } from 'react-redux';

import useAuth from '../../contexts/auth';
import { Dispatch } from '../../stores';
import Segment from '../../components/Segment';
import Home from '../../pages/Home';
import Layout from '../Layout';
import Loading from './Loading';

const Login = lazy(() => import('../../pages/Auth/Login'));
const Logout = lazy(() => import('../../pages/Auth/Logout'));
const Orders = lazy(() => import('../../pages/Orders'));
const OrderCreate = lazy(() => import('../../pages/Orders/OrderCreate'));
const OrderUpdate = lazy(() => import('../../pages/Orders/OrderUpdate'));
const OrderDelete = lazy(() => import('../../pages/Orders/OrderDelete'));
const Shipments = lazy(() => import('../../pages/Shipments'));
const OrderImport = lazy(() => import('../../pages/Orders/OrderImport'));
const Settings = lazy(() => import('../../pages/Settings'));

const mapDispatch = (dispatch: Dispatch) => ({
  getCustomerInfo: async () => await dispatch.customer.getCustomerInfo(),
});

type DispatchProps = ReturnType<typeof mapDispatch>;
interface Props extends DispatchProps {}

const App: React.FC<Props> = ({ getCustomerInfo }) => {
  const [{ isAuthenticated }] = useAuth();
  const [isLoaded, setLoaded] = useState(false);
  const history = useHistory();

  useEffect(() => {
    setLoaded(true);
    if (isLoaded && !isAuthenticated) {
      history.push('/login');
    } else if (isAuthenticated) {
      getCustomerInfo();
    }
  }, [history, isAuthenticated, isLoaded, getCustomerInfo]);

  return (
    <Fragment>
      <Segment />
      <Layout>
        <Suspense
          fallback={
            <main>
              <Loading />
            </main>
          }
        >
          {isLoaded && isAuthenticated ? (
            <Switch>
              <Route exact path="/" component={Home}>
                <Redirect to="/orders" />
              </Route>
              <Route path="/logout" component={Logout} />
              <Route path="/orders/create" component={OrderCreate} />
              <Route path="/orders/import" component={OrderImport} />
              <Route path="/orders/:id/delete" component={OrderDelete} />
              <Route path="/orders/:id" component={OrderUpdate} />
              <Route path="/orders" component={Orders} />
              <Route path="/shipments" component={Shipments} />
              <Route path="/settings" component={Settings} />
            </Switch>
          ) : (
            <Route
              path="/login"
              render={(props) => <Login {...props} isAuthenticated={isAuthenticated} />}
            />
          )}
        </Suspense>
      </Layout>
    </Fragment>
  );
};

export default connect(null, mapDispatch)(App);
